import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFulfillmentPlanInformation } from './use-fulfillment-plan-information';
import { emptyObject } from 'ui-components/default-props/empty';
import { usePicklistsFeatureGate } from '../../../../feature-gates/use-picklists-feature-gate';
import { getIsBatchDetailsPage } from '../../../../store/batches/batches.helpers';
import Global from '../../../../store/global-selectors';
import { labelQuePrintPickListStart } from '../../../../store/label-que/label-que.actions';
import { printPickList } from '../../../../store/printing/print.actions';

/**
 * @callback BatchAction
 * @returns {(batchId: string, autoPrint: boolean) => object}
 */

/**
 * @param {string[]} fulfillmentPlanIds
 * @returns {BatchAction|undefined}
 */
export const useBatchAction = (fulfillmentPlanIds) => {
  const isOnBatchesGrid = getIsBatchDetailsPage();
  const { fulfillmentPlanSetId } = useSelector(Global.selectBatchDetailsBatch) || emptyObject;

  if (isOnBatchesGrid && fulfillmentPlanIds.length === 0 && fulfillmentPlanSetId) {
    return () => labelQuePrintPickListStart(fulfillmentPlanSetId);
  }
};

/**
 * @typedef {import('../../../../prop-types/fulfillment-plan').FulfillmentPlanPropType} FulfillmentPlanPropType
 */

/**
 * @param {{
 *   fulfillmentPlans: FulfillmentPlanPropType[],
 *   salesOrders: object[],
 *   isUseCustomerDocType?: boolean
 * }} params
 * @returns {{ isFeatureEnabled: boolean, isDisabled: boolean, onClick: () => void }}
 */
export const usePickListDisabledCheck = ({ fulfillmentPlans, salesOrders, isUseCustomerDocType = false }) => {
  const dispatch = useDispatch();
  const { fulfillmentPlanIds } = useFulfillmentPlanInformation(fulfillmentPlans);
  const isOnBatchesGrid = getIsBatchDetailsPage();
  const { isFeatureEnabled } = usePicklistsFeatureGate();
  const isDisabled = !isFeatureEnabled || (!isOnBatchesGrid && salesOrders.length === 0);

  const batchAction = useBatchAction(fulfillmentPlanIds);

  const onClick = useCallback(() => {
    if (batchAction) {
      dispatch(batchAction());
      return;
    }
    dispatch(printPickList(fulfillmentPlanIds, isUseCustomerDocType));
  }, [dispatch, fulfillmentPlanIds, batchAction, isUseCustomerDocType]);

  return useMemo(
    () => ({
      isFeatureEnabled,
      isDisabled,
      onClick,
    }),
    [isDisabled, isFeatureEnabled, onClick],
  );
};
